import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useHttpServicesQuery } from 'queries/admin/httpServices/useHttpServicesQuery'
import { useExecuteHttpServiceGetMutation } from 'queries/admin/httpServices/useExecuteHttpServiceGetMutation'

type HttpServicesContextState = {
  state: {
    services: NonNullable<
      ReturnType<typeof useHttpServicesQuery>['data']
    >['services']
    response: ReturnType<typeof useExecuteHttpServiceGetMutation>['data']
    isExecuting: boolean
    executeError: string | null
  }
  actions: {
    execute: ReturnType<typeof useExecuteHttpServiceGetMutation>['mutate']
  }
}

const [Context, useHttpServicesContextValue] =
  createContext<HttpServicesContextState>({ name: 'HttpServicesContext' })

export const HttpServicesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data, isPending, error } = useHttpServicesQuery()
  const execution = useExecuteHttpServiceGetMutation()

  if (isPending) {
    return <Loading />
  }

  if (!data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        {parseErrorMessage(error) ?? 'Failed to load HTTP services'}
      </Message>
    )
  }

  const value: HttpServicesContextState = {
    state: {
      services: data.services,
      response: execution.data,
      isExecuting: execution.isPending,
      executeError: execution.isError
        ? parseErrorMessage(execution.error) ?? 'Request failed'
        : null,
    },
    actions: {
      execute: execution.mutate,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useHttpServicesContextValue }
